/**
 * WP-2 H1 — Side-effect action handler registry.
 *
 * Keyed by action type. `ActionExecutor` dispatches non-UI actions through this
 * map; popup/offer actions (show_leads_popup, display_offer) stay in the shell
 * because they render React state. WP-3 registers commerce handlers
 * (add_to_cart, apply_discount) here without touching the dispatch shell.
 */

import type { ActionHandler } from './types';
import { STOREFRONT_SEMANTIC_ACTION_TYPES } from '@trafilea/tss-lib-funnel-schema-ts';
import { goToUrlHandler } from './go-to-url';
import { dispatchEventHandler } from './dispatch-event';
import { saveToStorageHandler } from './save-to-storage';
import { connectToSystemHandler } from './connect-to-system';
import { addToCartHandler } from './add-to-cart';
import { applyDiscountHandler } from './apply-discount';
import { captureLeadHandler } from './modules/capture-lead';
import { revealCouponHandler } from './modules/reveal-coupon';
import { runSpinHandler } from './modules/run-spin';
import { acceptOfferHandler } from './modules/accept-offer';
import { declineOfferHandler } from './modules/decline-offer';
import { dismissExperienceHandler } from './modules/dismiss-experience';
import { startCheckoutHandler } from './modules/start-checkout';
import { selectVariantHandler } from './modules/select-variant';
import { applyCouponHandler } from './modules/apply-coupon';
import { removeFromCartHandler } from './modules/remove-from-cart';

const handlers: ActionHandler[] = [
  goToUrlHandler,
  dispatchEventHandler,
  saveToStorageHandler,
  connectToSystemHandler,
  // WP-3 commerce handlers. add_to_cart is in the schema enum; apply_discount is
  // NOT (BFF apply-coupon proxy missing) but is registered so a stray artifact
  // fails loudly (typed `unsupported`) rather than silently.
  addToCartHandler,
  applyDiscountHandler,
  captureLeadHandler,
  revealCouponHandler,
  runSpinHandler,
  acceptOfferHandler,
  declineOfferHandler,
  dismissExperienceHandler,
  startCheckoutHandler,
  selectVariantHandler,
  applyCouponHandler,
  removeFromCartHandler,
];

function buildRegistry(entries: ActionHandler[]): Readonly<Record<string, ActionHandler>> {
  const registry: Record<string, ActionHandler> = {};
  for (const handler of entries) {
    if (registry[handler.type]) {
      throw new Error(`Duplicate funnel action handler: ${handler.type}`);
    }
    registry[handler.type] = handler;
  }
  for (const actionType of STOREFRONT_SEMANTIC_ACTION_TYPES) {
    if (!registry[actionType]) {
      throw new Error(`Missing storefront semantic handler: ${actionType}`);
    }
  }
  return Object.freeze(registry);
}

export const actionHandlerRegistry = buildRegistry(handlers);

export function getActionHandler(type: string): ActionHandler | undefined {
  return actionHandlerRegistry[type];
}

export * from './types';
export { MAX_EVENT_HOPS } from './dispatch-event';
